import styled from "styled-components";
import {
  Container,
  ContainerView,
  TitlePage,
} from "../../components/Container";
import { useApp } from "../../contexts";
import { useEffect, useMemo, useState } from "react";
import { OTP_METHOD } from "../../utils/otpMethod";
import { changeOtpMethod, disconnectLineOtp, ssoGetUserInfo } from "../../services/app";
import { useModalConfirm } from "../../components/ModalConfirm/useModalConfirm";
import * as Notification from "../../Notification";
import { useNavigate } from "react-router-dom";

const VerificationMethodItemStyled = styled.label`
  input[type="radio"]:checked ~ .verification-method--item__content {
    .verification-method--item__content__mark {
      display: block;
    }
  }
`;

export function OtpVerification() {
  const { T, user, reloadProfile, setLoading, loading } = useApp();
  const modalConfirm = useModalConfirm();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    verificationMethod: OTP_METHOD.EMAIL, // Default method
  });

  useEffect(() => {
    const otpMethod = user?.otpMethod ?? OTP_METHOD.EMAIL;
    setFormData({
      verificationMethod: otpMethod,
    });
  }, [user]);

  /**
   * https://developers.line.biz/en/docs/line-login/integrate-line-login/#making-an-authorization-request
   */
  const getLineLoginUrl = () => {
    const baseUrl = process.env.REACT_APP_LINE_LOGIN_URL;
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_LINE_REDIRECT_URI;
    const state = "a2v-line-1752204462471";
    const scope = ["openid", "profile"].join("%20");
    const bot_prompt = "aggressive";
    const prompt = "consent";
    return `${baseUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=${scope}&prompt=${prompt}&bot_prompt=${bot_prompt}`;
  };

  const btnConnectLineOnClick = async () => {
    const hasPassword = await checkUserHasPassword();
    if (hasPassword) {
      const lineLoginUrl = getLineLoginUrl();
      // save current href to localstorage
      localStorage.setItem("href_before_connect_line", window.location.href);
      
      // Open the LINE login URL in a new tab
      window.location.href = lineLoginUrl;
    } else {
      handleSetPassword();
    }
  };

  const checkUserHasPassword = async () => {
      try {
        setLoading(true);
        const res = await ssoGetUserInfo();
        return Boolean(res?.data?.data?.has_password);
      } catch (error) {
        console.error("Error checking user password:", error);
        return true;      
      } finally {
        setLoading(false);
      }
    };
  
    const handleSetPassword = async () => {
      const confirm = await modalConfirm.confirm({
        title: T("Set password"),
        message: T(
          "Kindly create a password before linking your LINE account"
        ),
        confirmButtonTitle: T("OK"),
        cancelButtonTitle: T("Cancel"),
      });
      if (confirm) {
        navigate("/change-password");
      }
    }

  const btnDisconnectLineOnClick = async () => {
    const confirm = await modalConfirm.confirm({
      title: T("Disconnect LINE"),
      icon: (
        <div className="flex justify-center items-center w-10 h-10">
          <img
            src={"/assets/images/line.png"}
            className="object-contain  w-full h-auto"
            alt="LINE"
          />
        </div>
      ),
      message: T(
        "Are you sure you want to disconnect your LINE account? You will no longer receive verification codes through LINE messages."
      ),
      confirmButtonTitle: T("Confirm"),
      cancelButtonTitle: T("Cancel"),
    });
    if (confirm) {
      try {
        setLoading(true);
        const res = await disconnectLineOtp();
        if (res?.data?.status) {
          await reloadProfile();
          Notification.success(T("Disconnected LINE account successfully."));
        } else {
          Notification.error(
            T(res?.data?.message ?? "Failed to disconnect LINE account.")
          );
        }
      } catch (error) {
        console.error("Error disconnecting Line account:", error);
        Notification.error(
          T(
            error?.response?.data?.message ??
              "Something went wrong! Please try again later!"
          )
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const formOnSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await changeOtpMethod(formData.verificationMethod);
      if (res?.data?.status) {
        // Reload user profile or update state to reflect the change
        await reloadProfile();
        Notification.success(T("Changed OTP method successfully."));
      } else {
        Notification.error(
          T(res?.data?.message ?? "Failed to change OTP method.")
        );
      }
    } catch (error) {
      console.error("Error changing OTP method:", error);
      Notification.error(
        T(
          error?.response?.data?.message ??
            "Something went wrong! Please try again later!"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const otpMethods = useMemo(
    () => [
      {
        value: OTP_METHOD.EMAIL,
        icon: (
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 32 33"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.71875 8.5957L11.7436 13.7092C15.0707 15.5944 16.4765 15.5944 19.8036 13.7092L28.8284 8.5957"
              stroke="#222222"
              strokeWidth="1.95822"
              strokeLinejoin="round"
            />
            <path
              d="M2.73933 18.355C2.82468 22.357 2.86735 24.358 4.34402 25.8403C5.82069 27.3226 7.87584 27.3742 11.9862 27.4775C14.5194 27.5412 17.0278 27.5412 19.561 27.4775C23.6713 27.3742 25.7265 27.3226 27.2031 25.8403C28.6798 24.358 28.7225 22.357 28.8078 18.355C28.8353 17.0682 28.8353 15.7891 28.8078 14.5023C28.7225 10.5003 28.6798 8.49926 27.2031 7.01697C25.7265 5.53468 23.6713 5.48305 19.561 5.37977C17.0277 5.31612 14.5194 5.31612 11.9861 5.37976C7.87584 5.48303 5.82069 5.53466 4.34401 7.01695C2.86734 8.49924 2.82467 10.5002 2.73933 14.5022C2.71189 15.789 2.71189 17.0682 2.73933 18.355Z"
              stroke="#222222"
              strokeWidth="1.95822"
              strokeLinejoin="round"
            />
          </svg>
        ),
        title: (
          <>
            <div className="flex flex-col gap-1">
              <div className="text-[16px] lg:text-[22px] font-semibold">
                {T("Send to your registered email")}
              </div>
              <div className="text-[14px]/[18px] lg:text-[16px] text-[#000000B2]">
                {user?.email}
              </div>
            </div>
          </>
        ),
      },
      {
        value: OTP_METHOD.LINE,
        disabled: !user?.lineUserId,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            width="1em"
            height="1em"
          >
            <path
              fill="#00c300"
              transform="scale(1.2) translate(-4,-4)"
              d="M12.5,42h23c3.59,0,6.5-2.91,6.5-6.5v-23C42,8.91,39.09,6,35.5,6h-23C8.91,6,6,8.91,6,12.5v23C6,39.09,8.91,42,12.5,42z"
            />
            <path
              fill="#fff"
              transform="scale(1.2) translate(-4,-4)"
              d="M37.113,22.417c0-5.865-5.88-10.637-13.107-10.637s-13.108,4.772-13.108,10.637c0,5.258,4.663,9.662,10.962,10.495c0.427,0.092,1.008,0.282,1.155,0.646c0.132,0.331,0.086,0.85,0.042,1.185c0,0-0.153,0.925-0.187,1.122c-0.057,0.331-0.263,1.296,1.135,0.707c1.399-0.589,7.548-4.445,10.298-7.611h-0.001C36.203,26.879,37.113,24.764,37.113,22.417z M18.875,25.907h-2.604c-0.379,0-0.687-0.308-0.687-0.688V20.01c0-0.379,0.308-0.687,0.687-0.687c0.379,0,0.687,0.308,0.687,0.687v4.521h1.917c0.379,0,0.687,0.308,0.687,0.687C19.562,25.598,19.254,25.907,18.875,25.907z M21.568,25.219c0,0.379-0.308,0.688-0.687,0.688s-0.687-0.308-0.687-0.688V20.01c0-0.379,0.308-0.687,0.687-0.687s0.687,0.308,0.687,0.687V25.219z M27.838,25.219c0,0.297-0.188,0.559-0.47,0.652c-0.071,0.024-0.145,0.036-0.218,0.036c-0.215,0-0.42-0.103-0.549-0.275l-2.669-3.635v3.222c0,0.379-0.308,0.688-0.688,0.688c-0.379,0-0.688-0.308-0.688-0.688V20.01c0-0.296,0.189-0.558,0.47-0.652c0.071-0.024,0.144-0.035,0.218-0.035c0.214,0,0.42,0.103,0.549,0.275l2.67,3.635V20.01c0-0.379,0.309-0.687,0.688-0.687c0.379,0,0.687,0.308,0.687,0.687V25.219z M32.052,21.927c0.379,0,0.688,0.308,0.688,0.688c0,0.379-0.308,0.687-0.688,0.687h-1.917v1.23h1.917c0.379,0,0.688,0.308,0.688,0.687c0,0.379-0.309,0.688-0.688,0.688h-2.604c-0.378,0-0.687-0.308-0.687-0.688v-2.603c0-0.001,0-0.001,0-0.001c0,0,0-0.001,0-0.001v-2.601c0-0.001,0-0.001,0-0.002c0-0.379,0.308-0.687,0.687-0.687h2.604c0.379,0,0.688,0.308,0.688,0.687s-0.308,0.687-0.688,0.687h-1.917v1.23H32.052z"
            />
          </svg>
        ),
        title: (
          <>
            <div className="flex flex-col gap-1">
              <div className="text-[16px] lg:text-[22px] font-semibold">
                {user?.lineUserId ? T("Send message to your LINE") : T("Want to receive OTP with LINE?")}
              </div>
              <div className="text-[14px]/[18px] lg:text-[16px] text-[#000000B2]">
                {user?.lineUserId
                  ? user?.lineDisplayName
                  : T("Complete setup to enable LINE verification")}
              </div>
              {user?.lineUserId && (
                <div className="mt-3">
                  <button
                    className="h-6 lg:h-[32px] rounded-full bg-[#FF0017] text-white text-[12px] font-semibold px-4 lg:px-6"
                    type="button"
                    onClick={btnDisconnectLineOnClick}
                  >
                    {T("Disconnect")}
                  </button>
                </div>
              )}
            </div>
          </>
        ),
        content: (
          <>
            {!user?.lineUserId && (
              <div>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2 items-center">
                    <span className="inline-block w-5 h-5 bg-[#00C200] text-white rounded-full leading-5 text-center flex-grow-0 flex-shrink-0 basis-5">
                      1
                    </span>
                    <span className="text-[#000000B2]">
                      {T("Link your LINE account to A2V account")}
                    </span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="inline-block w-5 h-5 bg-[#00C200] text-white rounded-full leading-5 text-center flex-grow-0 flex-shrink-0 basis-5">
                      2
                    </span>
                    <span className="text-[#000000B2]">
                      {T("Add friend to receive message from A2V")}
                    </span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="inline-block w-5 h-5 bg-[#00C200] text-white rounded-full leading-5 text-center flex-grow-0 flex-shrink-0 basis-5">
                      3
                    </span>
                    <span className="text-[#000000B2]">
                      {T("Set as your default method (optional)")}
                    </span>
                  </div>
                </div>
                <div className="mt-4 ">
                  <button
                    className="bg-[#00C200] text-white px-4 py-2 h-[45px] w-full rounded-full flex items-center justify-center gap-1 font-semibold text-[16px] text-decoration-none hover:text-white"
                    type="button"
                    onClick={btnConnectLineOnClick}
                  >
                    {T("Start setup now")}
                  </button>
                </div>
              </div>
            )}
          </>
        ),
      },
    ],
    [T, user]
  );

  const isOnlyOneMethodSelectable = useMemo(() => {
    return otpMethods.filter((method) => !method.disabled).length <= 1;
  }, [otpMethods]);

  return (
    <Container>
      <ContainerView background="transparent">
        <TitlePage>{T("Setup verification method")}</TitlePage>
        <div className="flex justify-between items-start w-full container mt-[32px] lg:gap-12">
          <div className="w-full grow-1 flex justify-center">
            <form
              className="form w-full lg:max-w-[650px]"
              onSubmit={formOnSubmit}
            >
              <div className="form__note lg:text-[22px]">
                {T("Please choose a default method to receive the OTP:")}
              </div>
              <div className="form__controls mt-7">
                <div className="form__control__item form__control__item--verification-method">
                  {otpMethods.map((method, index) => (
                    <VerificationMethodItemStyled
                      key={index}
                      htmlFor={`verification-method-${method.value}`}
                      className="verification-method--item flex items-center gap-4 mb-4 lg:mb-7"
                    >
                      <input
                        type="radio"
                        name="verification-method"
                        className="verification-method--item__control w-0 h-0 opacity-0 absolute"
                        id={`verification-method-${method.value}`}
                        value={method.value}
                        checked={formData.verificationMethod === method.value}
                        disabled={method.disabled}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            verificationMethod: method.value,
                          })
                        }
                      />
                      <div className="verification-method--item__content transition-all px-4 py-2 border border-[#0000004D] rounded-[12px] cursor-pointer w-full lg:px-6 lg:py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3 lg:gap-4">
                            <div className="text-[24px] mt-3 lg:text-[32px]">
                              {method.icon}
                            </div>
                            <div>{T(method.title)}</div>
                          </div>
                          <div className="w-4 h-4 lg:w-7 lg:h-7 rounded-full border-[2px] border-[#D0D0D0] flex items-center justify-center mt-3">
                            <div className="w-2 h-2 lg:w-4 lg:h-4 bg-[#000] rounded-full hidden verification-method--item__content__mark"></div>
                          </div>
                        </div>
                        <div className="mt-4">{method.content}</div>
                      </div>
                    </VerificationMethodItemStyled>
                  ))}
                </div>
              </div>
              <div className="form__actions flex justify-center items-center mt-7">
                <button
                  type="submit"
                  className="h-[55px] w-full lg:w-[380px] bg-[#000000] text-white text-[14px] uppercase font-bold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || isOnlyOneMethodSelectable}
                >
                  {T("Save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </ContainerView>
    </Container>
  );
}
